import AddIcon from "@mui/icons-material/AddRounded";
import { Button, Typography } from "@mui/material";
import type {
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridValueGetter,
  GridValueSetter,
} from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import type { ObjectMetricExtension } from "../../../../api/dataCollectionTypes";
import type { NumericGridTableBlockRow, ScenarioTableBlockColumn } from "../../../../api/inputFormTypes";
import type { MetricFormatConfiguration } from "../../../../api/portfolioMonitoringTypes";
import useDefinedContext from "../../../../hooks/useDefinedContext";
import useSingleClickGridEditing from "../../../../hooks/useSingleClickGridEditing";
import { makeTintedBackgroundFromColor } from "../../../../utilities/colorHelper";
import { makeNextEditableCellEdited } from "../../../../utilities/editableDataGridHelper";
import { formatMetricNumberValue } from "../../../../utilities/metricFormatters";
import { defined } from "../../../../utilities/typeHelper";
import DataGrid from "../../../grid/DataGrid";
import { getSubmissionDataGridFormStyles } from "../submissionTableFormStyles";
import useDataSubmissionFormContext from "../useDataSubmissionFormContext";
import AddMetricExtensionDialog from "./AddMetricExtensionDialog";
import NewMetricExtensionRowTag from "./NewMetricExtensionRowTag";
import PortfolioMonitoringInputTableContext, {
  PortfolioMonitoringInputTableContextProvider,
} from "./PortfolioMonitoringInputTableContext";
import ScenarioColumnHeader from "./ScenarioColumnHeader";
import { calculateTotals } from "./tableTotalsCalculator";

type RowModel = NumericGridTableBlockRow & {
  isNew?: boolean;
};

const NameCell = ({ row }: { row: RowModel }) => {
  const { isSubmissionEditable } = useDataSubmissionFormContext();
  const { onAddMetricExtensions } = useDefinedContext(PortfolioMonitoringInputTableContext);

  if (row.type === "Metric" || row.type === "MetricExtension") {
    return (
      <>
        <Typography noWrap sx={{ pl: row.indentationLevel * 2, flexGrow: 1 }}>
          {row.name}
        </Typography>
        {row.isNew && <NewMetricExtensionRowTag />}
      </>
    );
  }

  if (row.type === "ExtendedMetricSection") {
    return (
      <>
        <Typography variant="subtitle2" noWrap sx={{ pl: row.indentationLevel * 2, flexGrow: 1 }}>
          {row.name}
        </Typography>
        {isSubmissionEditable && (
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => onAddMetricExtensions([row.metricId], row.name)}
            tabIndex={-1}
          >
            Add
          </Button>
        )}
      </>
    );
  }

  if (row.type === "Total" || row.type === "Section") {
    return (
      <Typography variant="subtitle2" noWrap sx={{ pl: row.indentationLevel * 2 }}>
        {row.name}
      </Typography>
    );
  }

  return null;
};

interface ValuesDiffProps {
  previous: number;
  current: number;
  formatConfig: MetricFormatConfiguration | undefined;
}

const ValuesDiff = ({ previous, current, formatConfig }: ValuesDiffProps) => {
  return (
    <Typography component="span">
      <Typography component="span" sx={(t) => ({ bgcolor: makeTintedBackgroundFromColor(t, t.palette.error.main) })}>
        {formatMetricNumberValue(previous, formatConfig)}
      </Typography>
      <Typography component="span" sx={(t) => ({ bgcolor: makeTintedBackgroundFromColor(t, t.palette.success.main) })}>
        {formatMetricNumberValue(current, formatConfig)}
      </Typography>
    </Typography>
  );
};

interface ScenarioCellProps {
  columnId: string;
  row: RowModel;
  value: number | null | undefined;
  formattedValue: string;
}

const ScenarioCellWithValuesDiff = ({ columnId, row, value, formattedValue }: ScenarioCellProps) => {
  if (typeof value !== "number") {
    return formattedValue;
  }

  if (row.type !== "Metric" && row.type !== "MetricExtension") {
    return formattedValue;
  }

  const previousValue = row.previousValues[columnId];
  if (!previousValue || previousValue === value) {
    return formattedValue;
  }

  return <ValuesDiff previous={previousValue} current={value} formatConfig={row.defaultFormat} />;
};

const getColumns = (
  columnDefinitions: ScenarioTableBlockColumn[],
  isSubmissionEditable: boolean
): GridColDef<RowModel>[] => {
  const nameColumn: GridColDef<RowModel> = {
    field: "name",
    headerName: "Metric",
    flex: 2,
    minWidth: 300,
    editable: false,
    cellClassName: ({ row }) => {
      if (row.type === "Total") {
        return "total-cell";
      }

      if (row.type === "Section") {
        return "";
      }

      if (row.isSecondary) {
        return "secondary-name-cell";
      }

      return "";
    },
    renderCell: ({ row }) => <NameCell row={row} />,
  };

  const valueFormatter = (value: number | null | undefined, row: RowModel) => {
    if (row.type === "Total") {
      return typeof value !== "number" ? "-" : formatMetricNumberValue(value, { numberOfDecimals: 0 });
    }

    const formatConfig = row.type === "Metric" || row.type === "MetricExtension" ? row.defaultFormat : undefined;
    return typeof value === "number" ? formatMetricNumberValue(value, formatConfig) : "";
  };

  const valueGetter: GridValueGetter<RowModel, number | null | undefined> = (_, row, col) => {
    if (row.type === "Total" || row.type === "Metric" || row.type === "MetricExtension") {
      return row.values[col.field];
    }

    return undefined;
  };

  const valueSetter: GridValueSetter<RowModel, number | null | undefined> = (newValue, row, col) => {
    if (row.type === "Metric" || row.type === "MetricExtension") {
      return {
        ...row,
        values: {
          ...row.values,
          [col.field]: newValue ?? undefined,
        },
      } as RowModel;
    }

    return row;
  };

  const editableCellClassName = ({ row }: { row: RowModel }) => (row.type === "Total" ? "total-cell" : "");

  const readonlyCellClassName = ({ row }: { row: RowModel }) =>
    row.type === "Total"
      ? "total-cell"
      : row.type === "Metric" || row.type === "MetricExtension"
        ? "readonly-cell"
        : "";

  const mapScenarioColumn = (
    columnDef: ScenarioTableBlockColumn,
    isSubmissionEditable: boolean
  ): GridColDef<RowModel> => ({
    field: columnDef.id,
    flex: 1,
    minWidth: 160,
    type: "number",
    editable: isSubmissionEditable && columnDef.isEditable,
    colSpan: (_, row) => (row.type === "Section" || row.type === "ExtendedMetricSection" ? 8 : 0),
    renderHeader: () => <ScenarioColumnHeader columnDefinition={columnDef} />,
    headerClassName: columnDef.isEditable ? "" : "readonly-header",
    cellClassName: columnDef.isEditable ? editableCellClassName : readonlyCellClassName,
    valueGetter,
    valueSetter,
    valueFormatter,
    renderCell:
      columnDef.isEditable && columnDef.dateRange === "PeriodShift"
        ? ({ row, formattedValue, value, field }: GridCellParams<RowModel, number, string>) => (
            <ScenarioCellWithValuesDiff
              columnId={field}
              row={row}
              value={value}
              formattedValue={formattedValue ?? ""}
            />
          )
        : undefined,
  });

  const scenarioColumns: GridColDef<RowModel>[] = columnDefinitions.map((columnDef) =>
    mapScenarioColumn(columnDef, isSubmissionEditable)
  );

  return [nameColumn, ...scenarioColumns];
};

const getRows = (rows: NumericGridTableBlockRow[]): RowModel[] => {
  return calculateTotals(rows);
};

interface Props {
  blockId: string;
  columnDefinitions: ScenarioTableBlockColumn[];
  rowDefinitions: NumericGridTableBlockRow[];
}

interface AddExtensionDialogState {
  metricIds: string[];
  metricName: string;
  addedExtensionIds: string[];
}

const MonthlyFinancialsInputForm = ({ blockId, columnDefinitions, rowDefinitions }: Props) => {
  const { isSubmissionEditable, onBlockCellValuesEdit } = useDataSubmissionFormContext();

  const [rows, setRows] = useState<RowModel[]>(() => getRows(rowDefinitions));
  const [dialogState, setDialogState] = useState<AddExtensionDialogState>();

  const columns = useMemo(
    () => getColumns(columnDefinitions, isSubmissionEditable),
    [columnDefinitions, isSubmissionEditable]
  );

  const { cellModesModel, setCellModesModel, handleCellClick } = useSingleClickGridEditing();

  const isCellEditable = useCallback(
    ({ row }: { row: RowModel }) => row.type === "Metric" || row.type === "MetricExtension",
    []
  );

  const handleRowUpdate = useCallback(
    (newRow: RowModel, oldRow: RowModel) => {
      if (
        (newRow.type !== "Metric" && newRow.type !== "MetricExtension") ||
        (oldRow.type !== "Metric" && oldRow.type !== "MetricExtension")
      ) {
        return newRow;
      }

      const updatedRow = { ...newRow };

      const updatedValueEntry = Object.entries(updatedRow.values).find(
        ([columnId, currentValue]) => oldRow.values[columnId] !== currentValue
      );

      if (updatedValueEntry !== undefined) {
        const metricId = updatedRow.metricId;
        const [columnId, newValue] = updatedValueEntry;
        const metricExtensionId = updatedRow.type === "MetricExtension" ? updatedRow.extensionId : undefined;

        onBlockCellValuesEdit(blockId, [{ metricId, metricExtensionId, columnId, valueUserInput: newValue ?? 0 }]);
      }

      setTimeout(
        () =>
          setRows((prevRows) => {
            const newRows = prevRows.map((row) => {
              if (row.id !== updatedRow.id) {
                return row;
              }

              return { ...updatedRow };
            });

            return calculateTotals(newRows);
          }),
        100
      );

      return updatedRow;
    },
    [blockId, onBlockCellValuesEdit]
  );

  const handleAddMetricExtensions = useCallback(
    (metricIds: string[], metricName: string) => {
      const addedExtensionIds = rows
        .map((r) => (r.type === "MetricExtension" && metricIds.includes(r.metricId) ? r.extensionId : ""))
        .filter(Boolean);

      setDialogState({
        metricIds,
        metricName,
        addedExtensionIds,
      });
    },
    [rows]
  );

  const handleMetricExtensionAdded = useCallback(
    (addedExtensions: ObjectMetricExtension[], isNew: boolean) => {
      setDialogState(undefined);

      if (addedExtensions.length === 0) {
        return;
      }

      const addedExtension = defined(addedExtensions[0]);

      const cellValues: Array<[string, number]> = columnDefinitions
        .filter((colDef) => colDef.isEditable)
        .map((colDef) => [colDef.id, 0]);

      setRows((prevRows) =>
        prevRows.reduce<RowModel[]>((result, row) => {
          if (row.id !== addedExtension.metricId) {
            return [...result, row];
          }

          const newExtensionRow: RowModel = {
            type: "MetricExtension",
            isNew,
            id: `${addedExtension.metricId}_${addedExtension.value}`,
            extensionId: addedExtension.id,
            metricId: addedExtension.metricId,
            name: addedExtension.value,
            indentationLevel: row.indentationLevel + 1,
            values: Object.fromEntries(cellValues),
            previousValues: {},
            dataType: "Money", // TODO get from metric configuration
          };

          return [...result, row, newExtensionRow];
        }, [])
      );

      onBlockCellValuesEdit(
        blockId,
        cellValues.map(([columnId, valueUserInput]) => ({
          metricId: addedExtension.metricId,
          metricExtensionId: addedExtension.id,
          columnId,
          valueUserInput,
        }))
      );
    },
    [blockId, columnDefinitions, onBlockCellValuesEdit]
  );

  const handleCellKeyDown: GridEventListener<"cellKeyDown"> = useCallback(
    (params: GridCellParams<RowModel>, event) => {
      if (event.code !== "Tab") {
        return;
      }

      event.defaultMuiPrevented = true;
      event.preventDefault();
      const direction = event.shiftKey ? "previous" : "next";
      makeNextEditableCellEdited(direction, rows, columns, params, setCellModesModel, isCellEditable, (r) => r.id);
    },
    [columns, isCellEditable, rows, setCellModesModel]
  );

  return (
    <PortfolioMonitoringInputTableContextProvider onAddMetricExtensions={handleAddMetricExtensions}>
      <DataGrid<RowModel>
        columns={columns}
        rows={rows}
        columnHeaderHeight={52}
        pinnedColumns={{ left: ["name"] }}
        cellModesModel={cellModesModel}
        onCellModesModelChange={setCellModesModel}
        onCellClick={handleCellClick}
        onCellKeyDown={handleCellKeyDown}
        disableColumnReorder
        disableColumnSorting
        showCellVerticalBorder
        showColumnVerticalBorder
        editMode="cell"
        isCellEditable={isCellEditable}
        processRowUpdate={handleRowUpdate}
        noScrollableContainer
        sx={getSubmissionDataGridFormStyles}
      />

      {dialogState && (
        <AddMetricExtensionDialog
          metricIds={dialogState.metricIds}
          metricName={dialogState.metricName}
          addedExtensionIds={dialogState.addedExtensionIds}
          allowCreatingExtensionValues
          onAdded={handleMetricExtensionAdded}
          onClose={() => setDialogState(undefined)}
        />
      )}
    </PortfolioMonitoringInputTableContextProvider>
  );
};

export default MonthlyFinancialsInputForm;
